import React, { useState } from 'react';
import {
  MessageSquareHeart,
  Search,
  Filter,
  CheckCircle2,
  Clock,
  AlertCircle,
  Star,
  Send,
  User,
  Building,
  Sparkles,
} from 'lucide-react';
import { UserFeedback } from '../../types';
import { INITIAL_USER_FEEDBACK } from '../../data/adminModulesData';

export const AdminFeedbackView: React.FC = () => {
  const [feedbacks, setFeedbacks] = useState<UserFeedback[]>(INITIAL_USER_FEEDBACK);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedFeedback, setSelectedFeedback] = useState<UserFeedback | null>(null);
  const [replyText, setReplyText] = useState('');
  const [newStatus, setNewStatus] = useState<UserFeedback['status']>('resolved');

  const filteredFeedbacks = feedbacks.filter((fb) => {
    const matchesSearch =
      fb.house_number.includes(searchQuery) ||
      fb.resident_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      fb.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      fb.ticket_number.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || fb.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const pendingCount = feedbacks.filter((f) => f.status === 'pending').length;
  const resolvedCount = feedbacks.filter((f) => f.status === 'resolved').length;
  const totalRated = feedbacks.filter((f) => f.resident_rating);
  const avgRating =
    totalRated.length > 0
      ? (totalRated.reduce((acc, curr) => acc + (curr.resident_rating || 0), 0) / totalRated.length).toFixed(1)
      : '5.0';

  const handleSendReply = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFeedback || !replyText.trim()) return;

    setFeedbacks((prev) =>
      prev.map((item) =>
        item.id === selectedFeedback.id
          ? {
              ...item,
              admin_reply: replyText,
              replied_at: new Date().toISOString(),
              replied_by: 'คุณศศิธร (ผู้จัดการนิติบุคคล)',
              status: newStatus,
            }
          : item
      )
    );

    setSelectedFeedback(null);
    setReplyText('');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-xl bg-rose-50 text-rose-600 border border-rose-200">
            <MessageSquareHeart className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-base sm:text-lg font-bold text-slate-900">
              5. ศูนย์รับฟังข้อเสนอแนะและเรื่องร้องเรียน (Resident Feedback & Grievance Hub)
            </h2>
            <p className="text-xs text-slate-500">
              ติดตามเรื่องร้องเรียน, ข้อเสนอแนะพัฒนาโครงการ, และตอบกลับลูกบ้านแบบเรียลไทม์
            </p>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="p-4 rounded-2xl bg-white border border-slate-200 shadow-xs space-y-1">
          <span className="text-xs text-slate-500">เรื่องทั้งหมด</span>
          <p className="text-2xl font-bold text-slate-900 font-mono">{feedbacks.length} เรื่อง</p>
          <p className="text-[11px] text-slate-400">หมุนเวียนในระบบ</p>
        </div>

        <div className="p-4 rounded-2xl bg-white border border-slate-200 shadow-xs space-y-1">
          <span className="text-xs text-slate-500">รอนิติตอบกลับ (Pending)</span>
          <p className="text-2xl font-bold text-amber-600 font-mono">{pendingCount}</p>
          <p className="text-[11px] text-amber-600 font-medium">เรื่องใหม่ที่ต้องดำเนินการ</p>
        </div>

        <div className="p-4 rounded-2xl bg-white border border-slate-200 shadow-xs space-y-1">
          <span className="text-xs text-slate-500">แก้ไขเสร็จสิ้นแล้ว</span>
          <p className="text-2xl font-bold text-emerald-700 font-mono">{resolvedCount}</p>
          <p className="text-[11px] text-emerald-600 font-medium">อัตราแก้ไขสำเร็จ 94%</p>
        </div>

        <div className="p-4 rounded-2xl bg-white border border-slate-200 shadow-xs space-y-1">
          <span className="text-xs text-slate-500">คะแนนความพึงพอใจ (CSAT)</span>
          <p className="text-2xl font-bold text-blue-700 font-mono flex items-center gap-1">
            {avgRating} <Star className="w-5 h-5 text-amber-400 fill-amber-400 inline" />
          </p>
          <p className="text-[11px] text-blue-600">จากการประเมินของลูกบ้าน</p>
        </div>
      </div>

      {/* Main Feedback List */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-xs overflow-hidden">
        <div className="p-4 border-b border-slate-200 flex flex-col md:flex-row items-center justify-between gap-3 bg-slate-50/50">
          <div className="relative w-full md:w-80">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="ค้นหาเลขที่บ้าน, รหัสเคส, หัวข้อ..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-xs rounded-xl border border-slate-200 bg-white"
            />
          </div>

          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="text-xs p-2 rounded-xl border border-slate-200 bg-white font-medium"
            >
              <option value="all">ทุกสถานะ ({feedbacks.length})</option>
              <option value="pending">รอดำเนินการ</option>
              <option value="investigating">กำลังตรวจสอบ</option>
              <option value="resolved">แก้ไขแล้ว</option>
            </select>
          </div>
        </div>

        <div className="divide-y divide-slate-100">
          {filteredFeedbacks.map((item) => (
            <div key={item.id} className="p-5 hover:bg-slate-50/60 transition-colors space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 rounded text-[10px] font-bold font-mono bg-slate-100 text-slate-700 border border-slate-200">
                    {item.ticket_number}
                  </span>
                  <span className="font-bold text-slate-800 text-sm">
                    บ้าน {item.house_number} ({item.resident_name})
                  </span>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-rose-50 text-rose-700 border border-rose-200">
                    {item.category === 'parking' && '🚗 การจอดรถ'}
                    {item.category === 'suggestion' && '💡 ข้อเสนอแนะ'}
                    {item.category === 'cleanliness' && '🧹 ความสะอาด/สวน'}
                    {item.category === 'noise_pet' && '🐕 สัตว์เลี้ยง/เสียงรบกวน'}
                    {item.category === 'security' && '🛡️ ความปลอดภัย'}
                    {item.category === 'juristic_service' && '🏢 งานบริการนิติ'}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <span
                    className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold ${
                      item.status === 'resolved'
                        ? 'bg-emerald-100 text-emerald-800'
                        : item.status === 'investigating'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-amber-100 text-amber-800'
                    }`}
                  >
                    {item.status === 'resolved' && '✅ แก้ไขแล้ว'}
                    {item.status === 'investigating' && '🔍 กำลังตรวจสอบ'}
                    {item.status === 'pending' && '⏳ รอดำเนินการ'}
                  </span>

                  <span className="text-[11px] text-slate-400 font-mono">
                    {new Date(item.created_at).toLocaleDateString('th-TH')}
                  </span>
                </div>
              </div>

              <div>
                <h4 className="font-bold text-slate-900 text-sm">{item.title}</h4>
                <p className="text-xs text-slate-600 mt-1 leading-relaxed bg-slate-50 p-3 rounded-xl">
                  {item.description}
                </p>
              </div>

              {/* Admin Reply Section */}
              {item.admin_reply && (
                <div className="p-3 bg-blue-50/60 rounded-xl border border-blue-100 space-y-1 text-xs">
                  <div className="flex items-center justify-between text-blue-900 font-bold">
                    <span>💬 คำตอบจากนิติบุคคล ({item.replied_by})</span>
                    <span className="text-[10px] text-blue-500 font-normal">
                      {item.replied_at && new Date(item.replied_at).toLocaleString('th-TH')}
                    </span>
                  </div>
                  <p className="text-blue-800 leading-relaxed">{item.admin_reply}</p>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-1">
                <button
                  onClick={() => {
                    setSelectedFeedback(item);
                    setReplyText(item.admin_reply || '');
                    setNewStatus(item.status);
                  }}
                  className="px-3 py-1.5 bg-blue-700 hover:bg-blue-800 text-white rounded-lg text-xs font-semibold shadow-xs flex items-center gap-1.5 cursor-pointer"
                >
                  <Send className="w-3.5 h-3.5" />
                  {item.admin_reply ? 'แก้ไขคำตอบ / เปลี่ยนสถานะ' : 'ตอบกลับลูกบ้าน & แก้ไข'}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Reply Modal */}
      {selectedFeedback && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-xs">
          <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl border border-slate-100 overflow-hidden">
            <div className="p-5 bg-slate-900 text-white flex items-center justify-between">
              <div>
                <h3 className="font-bold text-base">ตอบกลับเรื่องร้องเรียน/ข้อเสนอแนะ</h3>
                <p className="text-xs text-slate-400">
                  บ้าน {selectedFeedback.house_number} • {selectedFeedback.ticket_number}
                </p>
              </div>
              <button onClick={() => setSelectedFeedback(null)} className="text-slate-400 hover:text-white">✕</button>
            </div>

            <form onSubmit={handleSendReply} className="p-6 space-y-4 text-xs">
              <div className="p-3 bg-slate-50 rounded-xl">
                <span className="font-bold text-slate-800 block mb-0.5">{selectedFeedback.title}</span>
                <p className="text-slate-600">{selectedFeedback.description}</p>
              </div>

              <div>
                <label className="font-semibold text-slate-700 block mb-1">อัปเดตสถานะการดำเนินการ *</label>
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value as any)}
                  className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-semibold text-slate-800"
                >
                  <option value="investigating">🔍 กำลังตรวจสอบและประสานงาน</option>
                  <option value="resolved">✅ ดำเนินการแก้ไขเสร็จสิ้นเรียบร้อย</option>
                  <option value="closed">🔒 ปิดเรื่องเรียบร้อย</option>
                  <option value="pending">⏳ รอดำเนินการ</option>
                </select>
              </div>

              <div>
                <label className="font-semibold text-slate-700 block mb-1">ข้อความตอบกลับถึงลูกบ้าน (จะแจ้งเตือนในระบบลูกบ้าน) *</label>
                <textarea
                  rows={4}
                  required
                  placeholder="พิมพ์ข้อความตอบกลับหรือรายงานผลการดำเนินการ..."
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl leading-relaxed"
                />
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setSelectedFeedback(null)}
                  className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-100 font-medium"
                >
                  ยกเลิก
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 rounded-xl bg-blue-700 hover:bg-blue-800 text-white font-bold"
                >
                  ส่งคำตอบและบันทึก
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
